/* The Hope Tarot custom date picker. Progressive enhancement for date inputs. */
(function () {
  'use strict';

  var MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  var WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  var activePicker = null;
  var pickerId = 0;

  function pad(number) { return number < 10 ? '0' + number : String(number); }
  function toISO(year, month, day) { return year + '-' + pad(month + 1) + '-' + pad(day); }
  function todayISO() { var now = new Date(); return toISO(now.getFullYear(), now.getMonth(), now.getDate()); }
  function parseISO(value) {
    var match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || '');
    if (!match) return null;
    var date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    if (date.getFullYear() !== Number(match[1]) || date.getMonth() !== Number(match[2]) - 1 || date.getDate() !== Number(match[3])) return null;
    return date;
  }
  function displayDate(date) { return pad(date.getDate()) + '/' + pad(date.getMonth() + 1) + '/' + date.getFullYear(); }
  function spokenDate(date) { return date.getDate() + ' ' + MONTHS[date.getMonth()] + ' ' + date.getFullYear(); }
  function compareISO(a, b) { return String(a).localeCompare(String(b)); }
  function clamp(value, min, max) { return Math.min(Math.max(value, min), max); }
  function sameDay(a, b) { return a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate(); }
  function daysInMonth(year, month) { return new Date(year, month + 1, 0).getDate(); }

  function calendarIcon() {
    return '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M7 2v3M17 2v3M3.5 9h17M5.5 4h13a2 2 0 0 1 2 2v13a2 2 0 0 1-2 2h-13a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Z" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/></svg>';
  }

  function formatTyped(value) {
    var digits = String(value || '').replace(/\D/g, '').slice(0, 8);
    if (digits.length <= 2) return digits;
    if (digits.length <= 4) return digits.slice(0, 2) + '/' + digits.slice(2);
    return digits.slice(0, 2) + '/' + digits.slice(2, 4) + '/' + digits.slice(4);
  }

  function parseDisplay(value) {
    var match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value || '');
    if (!match) return null;
    var day = Number(match[1]);
    var month = Number(match[2]) - 1;
    var year = Number(match[3]);
    var date = new Date(year, month, day);
    if (date.getFullYear() !== year || date.getMonth() !== month || date.getDate() !== day) return null;
    return date;
  }

  function DatePicker(nativeInput) {
    this.native = nativeInput;
    this.id = 'hth-date-picker-' + (++pickerId);
    this.min = nativeInput.min || '1900-01-01';
    this.max = nativeInput.max || todayISO();
    this.native.min = this.min;
    this.native.max = this.max;
    this.selected = parseISO(nativeInput.value);
    this.view = this.selected ? new Date(this.selected.getFullYear(), this.selected.getMonth(), 1) : new Date();
    this.focusDate = this.selected || new Date();
    this.build();
    this.bind();
    this.syncFromNative();
    nativeInput.dataset.hthDatePicker = 'true';
  }

  DatePicker.prototype.build = function () {
    var label = this.native.closest ? this.native.closest('label') : null;
    var labelNode = label ? label.querySelector('span') : null;
    this.labelText = labelNode ? labelNode.textContent.trim() : 'Date';

    this.native.classList.add('hth-date-native');
    this.native.setAttribute('tabindex', '-1');
    this.native.setAttribute('aria-hidden', 'true');

    this.wrapper = document.createElement('div');
    this.wrapper.className = 'hth-date-picker';
    this.wrapper.innerHTML = '<input class="hth-date-picker__input" type="text" inputmode="numeric" autocomplete="bday" placeholder="DD/MM/YYYY" maxlength="10" aria-haspopup="dialog" aria-expanded="false" aria-controls="' + this.id + '" aria-label="' + this.labelText + ' in day month year format">' +
      '<button class="hth-date-picker__toggle" type="button" aria-label="Open calendar for ' + this.labelText + '" aria-controls="' + this.id + '" aria-expanded="false">' + calendarIcon() + '</button>' +
      '<p class="hth-date-picker__message" id="' + this.id + '-message" aria-live="polite"></p>';
    this.native.parentNode.insertBefore(this.wrapper, this.native.nextSibling);

    this.input = this.wrapper.querySelector('.hth-date-picker__input');
    this.toggle = this.wrapper.querySelector('.hth-date-picker__toggle');
    this.message = this.wrapper.querySelector('.hth-date-picker__message');
    this.input.setAttribute('aria-describedby', this.message.id);
    if (this.native.required) this.input.setAttribute('aria-required', 'true');

    this.backdrop = document.createElement('div');
    this.backdrop.className = 'hth-date-picker__backdrop';
    this.backdrop.hidden = true;

    this.panel = document.createElement('div');
    this.panel.className = 'hth-date-picker__panel';
    this.panel.id = this.id;
    this.panel.hidden = true;
    this.panel.setAttribute('role', 'dialog');
    this.panel.setAttribute('aria-modal', 'false');
    this.panel.setAttribute('aria-label', 'Choose ' + this.labelText);
    this.panel.innerHTML = '<div class="hth-date-picker__header">' +
      '<button class="hth-date-picker__nav" type="button" data-date-prev aria-label="Previous month">&#8249;</button>' +
      '<label><span class="hth-date-picker__sr-only">Month</span><select class="hth-date-picker__select" data-date-month aria-label="Month"></select></label>' +
      '<label><span class="hth-date-picker__sr-only">Year</span><select class="hth-date-picker__select" data-date-year aria-label="Year"></select></label>' +
      '<button class="hth-date-picker__nav" type="button" data-date-next aria-label="Next month">&#8250;</button>' +
      '</div>' +
      '<div class="hth-date-picker__weekdays" aria-hidden="true">' + WEEKDAYS.map(function (day) { return '<span class="hth-date-picker__weekday">' + day + '</span>'; }).join('') + '</div>' +
      '<div class="hth-date-picker__grid" role="grid"></div>' +
      '<div class="hth-date-picker__footer"><button type="button" data-date-clear>Clear</button><button class="is-primary" type="button" data-date-today>Use today</button></div>';
    document.body.appendChild(this.backdrop);
    document.body.appendChild(this.panel);

    this.monthSelect = this.panel.querySelector('[data-date-month]');
    this.yearSelect = this.panel.querySelector('[data-date-year]');
    this.grid = this.panel.querySelector('.hth-date-picker__grid');
    this.prev = this.panel.querySelector('[data-date-prev]');
    this.next = this.panel.querySelector('[data-date-next]');
    this.clearButton = this.panel.querySelector('[data-date-clear]');
    this.todayButton = this.panel.querySelector('[data-date-today]');

    this.monthSelect.innerHTML = MONTHS.map(function (month, index) { return '<option value="' + index + '">' + month + '</option>'; }).join('');
    var minDate = parseISO(this.min) || new Date(1900, 0, 1);
    var maxDate = parseISO(this.max) || new Date();
    var years = [];
    for (var year = maxDate.getFullYear(); year >= minDate.getFullYear(); year--) years.push('<option value="' + year + '">' + year + '</option>');
    this.yearSelect.innerHTML = years.join('');
  };

  DatePicker.prototype.bind = function () {
    var self = this;
    this.toggle.addEventListener('click', function () { self.isOpen() ? self.close(true) : self.open(true); });
    this.input.addEventListener('input', function () {
      var formatted = formatTyped(self.input.value);
      if (self.input.value !== formatted) self.input.value = formatted;
      self.applyManual(false);
    });
    this.input.addEventListener('blur', function () { self.applyManual(true); });
    this.input.addEventListener('keydown', function (event) {
      if ((event.altKey && event.key === 'ArrowDown') || event.key === 'ArrowDown') {
        event.preventDefault(); self.open(true);
      } else if (event.key === 'Escape' && self.isOpen()) {
        event.preventDefault(); self.close(false);
      }
    });
    this.native.addEventListener('change', function () { self.syncFromNative(); });
    this.prev.addEventListener('click', function () { self.changeMonth(-1); });
    this.next.addEventListener('click', function () { self.changeMonth(1); });
    this.monthSelect.addEventListener('change', function () { self.view.setMonth(Number(self.monthSelect.value)); self.render(); });
    this.yearSelect.addEventListener('change', function () { self.view.setFullYear(Number(self.yearSelect.value)); self.render(); });
    this.grid.addEventListener('click', function (event) {
      var button = event.target.closest ? event.target.closest('[data-date-value]') : null;
      if (!button || button.disabled) return;
      self.selectISO(button.getAttribute('data-date-value'));
    });
    this.grid.addEventListener('keydown', function (event) { self.onGridKeydown(event); });
    this.panel.addEventListener('keydown', function (event) { self.trapFocus(event); });
    this.clearButton.addEventListener('click', function () { self.clear(); self.close(true); self.input.focus(); });
    this.todayButton.addEventListener('click', function () { self.selectISO(todayISO()); });
    this.backdrop.addEventListener('click', function () { self.close(true); });
    window.addEventListener('resize', function () { if (self.isOpen()) self.position(); });
    window.addEventListener('scroll', function () { if (self.isOpen() && window.innerWidth > 640) self.position(); }, true);
    document.addEventListener('pointerdown', function (event) {
      if (!self.isOpen() || self.wrapper.contains(event.target) || self.panel.contains(event.target)) return;
      self.close(false);
    });
    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && self.isOpen()) { event.preventDefault(); self.close(true); }
    });
    var form = this.native.form;
    if (form) form.addEventListener('reset', function () { window.setTimeout(function () { self.syncFromNative(); }, 0); });
  };

  DatePicker.prototype.isOpen = function () { return !this.panel.hidden; };

  DatePicker.prototype.open = function (focusGrid) {
    if (activePicker && activePicker !== this) activePicker.close(false);
    activePicker = this;
    this.selected = parseISO(this.native.value);
    if (this.selected) {
      this.view = new Date(this.selected.getFullYear(), this.selected.getMonth(), 1);
      this.focusDate = new Date(this.selected.getFullYear(), this.selected.getMonth(), this.selected.getDate());
    }
    var accent = getComputedStyle(this.wrapper).getPropertyValue('--date-accent').trim();
    if (accent) this.panel.style.setProperty('--date-accent', accent);
    this.render();
    this.panel.hidden = false;
    this.backdrop.hidden = false;
    this.wrapper.classList.add('is-open');
    this.input.setAttribute('aria-expanded', 'true');
    this.toggle.setAttribute('aria-expanded', 'true');
    this.position();
    if (focusGrid) this.focusCurrentDay();
  };

  DatePicker.prototype.close = function (returnFocus) {
    if (!this.isOpen()) return;
    this.panel.hidden = true;
    this.backdrop.hidden = true;
    this.wrapper.classList.remove('is-open');
    document.body.classList.remove('hth-date-picker-lock');
    this.panel.setAttribute('aria-modal', 'false');
    this.input.setAttribute('aria-expanded', 'false');
    this.toggle.setAttribute('aria-expanded', 'false');
    if (activePicker === this) activePicker = null;
    if (returnFocus) this.toggle.focus();
  };

  DatePicker.prototype.position = function () {
    this.panel.style.removeProperty('left');
    this.panel.style.removeProperty('right');
    this.panel.style.removeProperty('top');
    this.panel.style.removeProperty('bottom');
    var isMobile = window.innerWidth <= 640;
    this.panel.setAttribute('aria-modal', isMobile ? 'true' : 'false');
    document.body.classList.toggle('hth-date-picker-lock', isMobile);
    if (isMobile) return;
    var rect = this.wrapper.getBoundingClientRect();
    var width = this.panel.offsetWidth || 372;
    var height = this.panel.offsetHeight || 430;
    var left = clamp(rect.left, 12, window.innerWidth - width - 12);
    var below = rect.bottom + 8;
    var top = below + height <= window.innerHeight - 12 ? below : Math.max(12, rect.top - height - 8);
    this.panel.style.left = Math.round(left) + 'px';
    this.panel.style.top = Math.round(top) + 'px';
  };

  DatePicker.prototype.trapFocus = function (event) {
    if (event.key !== 'Tab' || window.innerWidth > 640) return;
    var focusable = Array.prototype.slice.call(this.panel.querySelectorAll('button:not([disabled]), select:not([disabled]), [tabindex="0"]'))
      .filter(function (element) { return element.getClientRects().length > 0; });
    if (!focusable.length) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  DatePicker.prototype.render = function () {
    var self = this;
    var year = this.view.getFullYear();
    var month = this.view.getMonth();
    var minDate = parseISO(this.min);
    var maxDate = parseISO(this.max);
    var selected = parseISO(this.native.value);
    var today = parseISO(todayISO());
    this.monthSelect.value = String(month);
    this.yearSelect.value = String(year);
    this.grid.setAttribute('aria-label', MONTHS[month] + ' ' + year);

    var first = new Date(year, month, 1);
    var start = new Date(year, month, 1 - first.getDay());
    var html = [];
    for (var index = 0; index < 42; index++) {
      var date = new Date(start.getFullYear(), start.getMonth(), start.getDate() + index);
      var iso = toISO(date.getFullYear(), date.getMonth(), date.getDate());
      var disabled = (minDate && compareISO(iso, this.min) < 0) || (maxDate && compareISO(iso, this.max) > 0);
      var classes = ['hth-date-picker__day'];
      if (date.getMonth() !== month) classes.push('is-adjacent');
      if (sameDay(date, today)) classes.push('is-today');
      if (sameDay(date, selected)) classes.push('is-selected');
      var aria = spokenDate(date) + (sameDay(date, today) ? ', today' : '') + (sameDay(date, selected) ? ', selected' : '');
      html.push('<button type="button" role="gridcell" class="' + classes.join(' ') + '" data-date-value="' + iso + '" aria-label="' + aria + '"' + (sameDay(date, selected) ? ' aria-selected="true"' : ' aria-selected="false"') + (disabled ? ' disabled' : '') + ' tabindex="-1">' + date.getDate() + '</button>');
    }
    this.grid.innerHTML = html.join('');
    var currentMonthISO = toISO(year, month, 1);
    var previousMonth = new Date(year, month - 1, 1);
    var nextMonth = new Date(year, month + 1, 1);
    this.prev.disabled = !!minDate && compareISO(toISO(previousMonth.getFullYear(), previousMonth.getMonth(), daysInMonth(previousMonth.getFullYear(), previousMonth.getMonth())), this.min) < 0;
    this.next.disabled = !!maxDate && compareISO(toISO(nextMonth.getFullYear(), nextMonth.getMonth(), 1), this.max) > 0;
    this.todayButton.disabled = !!maxDate && compareISO(todayISO(), this.max) > 0;
    window.requestAnimationFrame(function () { if (self.isOpen()) self.position(); });
  };

  DatePicker.prototype.focusCurrentDay = function () {
    var targetISO = this.selected ? toISO(this.selected.getFullYear(), this.selected.getMonth(), this.selected.getDate()) : toISO(this.focusDate.getFullYear(), this.focusDate.getMonth(), this.focusDate.getDate());
    var target = this.grid.querySelector('[data-date-value="' + targetISO + '"]:not(:disabled)') || this.grid.querySelector('.hth-date-picker__day:not(:disabled)');
    if (target) { target.tabIndex = 0; target.focus(); }
  };

  DatePicker.prototype.changeMonth = function (amount) {
    this.view = new Date(this.view.getFullYear(), this.view.getMonth() + amount, 1);
    this.render();
    this.focusDate = new Date(this.view.getFullYear(), this.view.getMonth(), 1);
    this.focusCurrentDay();
  };

  DatePicker.prototype.onGridKeydown = function (event) {
    var button = event.target.closest ? event.target.closest('[data-date-value]') : null;
    if (!button) return;
    var date = parseISO(button.getAttribute('data-date-value'));
    if (!date) return;
    var nextDate = null;
    if (event.key === 'ArrowLeft') nextDate = new Date(date.getFullYear(), date.getMonth(), date.getDate() - 1);
    else if (event.key === 'ArrowRight') nextDate = new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1);
    else if (event.key === 'ArrowUp') nextDate = new Date(date.getFullYear(), date.getMonth(), date.getDate() - 7);
    else if (event.key === 'ArrowDown') nextDate = new Date(date.getFullYear(), date.getMonth(), date.getDate() + 7);
    else if (event.key === 'Home') nextDate = new Date(date.getFullYear(), date.getMonth(), date.getDate() - date.getDay());
    else if (event.key === 'End') nextDate = new Date(date.getFullYear(), date.getMonth(), date.getDate() + (6 - date.getDay()));
    else if (event.key === 'PageUp') nextDate = new Date(date.getFullYear(), date.getMonth() - 1, Math.min(date.getDate(), daysInMonth(date.getFullYear(), date.getMonth() - 1)));
    else if (event.key === 'PageDown') nextDate = new Date(date.getFullYear(), date.getMonth() + 1, Math.min(date.getDate(), daysInMonth(date.getFullYear(), date.getMonth() + 1)));
    else if (event.key === 'Escape') { event.preventDefault(); this.close(true); return; }
    else return;
    event.preventDefault();
    var iso = toISO(nextDate.getFullYear(), nextDate.getMonth(), nextDate.getDate());
    if (compareISO(iso, this.min) < 0 || compareISO(iso, this.max) > 0) return;
    this.focusDate = nextDate;
    if (nextDate.getMonth() !== this.view.getMonth() || nextDate.getFullYear() !== this.view.getFullYear()) {
      this.view = new Date(nextDate.getFullYear(), nextDate.getMonth(), 1);
      this.render();
    }
    var target = this.grid.querySelector('[data-date-value="' + iso + '"]');
    if (target) { button.tabIndex = -1; target.tabIndex = 0; target.focus(); }
  };

  DatePicker.prototype.selectISO = function (iso) {
    var date = parseISO(iso);
    if (!date || compareISO(iso, this.min) < 0 || compareISO(iso, this.max) > 0) return;
    this.native.value = iso;
    this.native.classList.remove('is-invalid');
    this.input.classList.remove('is-invalid');
    this.input.removeAttribute('aria-invalid');
    this.input.value = displayDate(date);
    this.selected = date;
    this.focusDate = date;
    this.message.textContent = 'Selected ' + spokenDate(date) + '.';
    this.native.dispatchEvent(new Event('input', { bubbles: true }));
    this.native.dispatchEvent(new Event('change', { bubbles: true }));
    this.close(false);
    this.input.focus();
  };

  DatePicker.prototype.applyManual = function (showError) {
    var value = this.input.value.trim();
    if (!value) {
      this.native.value = '';
      if (showError && this.native.required) this.setError('Enter a date in DD/MM/YYYY format.');
      else this.clearError();
      return;
    }
    var date = parseDisplay(value);
    if (!date) {
      this.native.value = '';
      if (showError || value.length === 10) this.setError('Enter a valid date in DD/MM/YYYY format.');
      return;
    }
    var iso = toISO(date.getFullYear(), date.getMonth(), date.getDate());
    if (compareISO(iso, this.min) < 0 || compareISO(iso, this.max) > 0) {
      this.native.value = '';
      this.setError('Choose a date between ' + displayDate(parseISO(this.min)) + ' and ' + displayDate(parseISO(this.max)) + '.');
      return;
    }
    this.clearError();
    this.native.value = iso;
    this.selected = date;
    this.view = new Date(date.getFullYear(), date.getMonth(), 1);
    this.native.dispatchEvent(new Event('input', { bubbles: true }));
    this.native.dispatchEvent(new Event('change', { bubbles: true }));
    this.message.textContent = 'Selected ' + spokenDate(date) + '.';
  };

  DatePicker.prototype.setError = function (message) {
    this.input.classList.add('is-invalid');
    this.native.classList.add('is-invalid');
    this.input.setAttribute('aria-invalid', 'true');
    this.message.classList.add('is-error');
    this.message.textContent = message;
  };

  DatePicker.prototype.clearError = function () {
    this.input.classList.remove('is-invalid');
    this.native.classList.remove('is-invalid');
    this.input.removeAttribute('aria-invalid');
    this.message.classList.remove('is-error');
    this.message.textContent = '';
  };

  DatePicker.prototype.clear = function () {
    this.native.value = '';
    this.input.value = '';
    this.selected = null;
    this.clearError();
    this.native.dispatchEvent(new Event('input', { bubbles: true }));
    this.native.dispatchEvent(new Event('change', { bubbles: true }));
  };

  DatePicker.prototype.syncFromNative = function () {
    var date = parseISO(this.native.value);
    this.selected = date;
    this.input.value = date ? displayDate(date) : '';
    if (date) {
      this.view = new Date(date.getFullYear(), date.getMonth(), 1);
      this.focusDate = date;
      this.clearError();
    }
  };

  function enhance(scope) {
    var root = scope && scope.querySelectorAll ? scope : document;
    var inputs = [];
    if (root.matches && root.matches('input[type="date"]')) inputs.push(root);
    Array.prototype.forEach.call(root.querySelectorAll('input[type="date"]'), function (input) { inputs.push(input); });
    inputs.forEach(function (input) {
      if (input.dataset.hthDatePicker) return;
      if (!input.name || !/(^|_)(dob|date_of_birth)$/i.test(input.name.replace(/[\[\] ]/g, '_')) && input.name.indexOf('DOB') === -1 && input.name.indexOf('Date of Birth') === -1) return;
      new DatePicker(input);
    });
  }

  function boot() {
    enhance(document);
    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        Array.prototype.forEach.call(mutation.addedNodes, function (node) {
          if (node.nodeType === 1) enhance(node);
        });
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  window.HopeDatePicker = { enhance: enhance };
})();
